import { Set } from "immutable";

import { Feature } from "@sol/features";
import { UserRole } from "@sol/sdk";

import { authenticatedFeatures } from "./UserRoleFeatureMap";

/**
 * Features a classroom bound role keeps when no classroom is assigned.
 *
 * A role missing from this dictionary is not bound to a classroom (administration, tutoring)
 * and therefore keeps every feature it already has. Roles listed here can only be narrowed:
 * having no classroom must never widen access.
 */
const FEATURES_WITHOUT_CLASSROOM: Partial<Record<UserRole, Feature[]>> = {
    [UserRole.LEARNER]: [
        ...authenticatedFeatures,
        Feature.HOMEPAGE,
        Feature.HOMEPAGE_LEARNER,
        Feature.MENU_LAYOUT_LEARNER,
        Feature.MENU_HOME,
        Feature.MY_WORKSPACE,
    ],
    // A trainer workspace (briefs, professional situations, frameworks, follow-ups) exists outside any classroom.
    [UserRole.TRAINER]: [
        ...authenticatedFeatures,
        Feature.HOMEPAGE,
        Feature.HOMEPAGE_TRAINER,
        Feature.HOMEPAGE_BRIEFS_CARD,
        Feature.HOMEPAGE_FRAMEWORKS_CARD,
        Feature.MENU_HOME,
        Feature.MENU_TRAINER_SPACE,
        Feature.MENU_QUICK_LINKS_BRIEF_CREATE,
        Feature.MENU_QUICK_LINKS_PROFESSIONAL_SITUATION_CREATE,
        Feature.MENU_FOLLOW_UPS,
        Feature.NOTIFICATIONS,
        Feature.BRIEFS,
        Feature.BRIEFS_CREATE,
        Feature.BRIEFS_EDIT,
        Feature.BRIEFS_EXPLORE,
        Feature.BRIEFS_MANAGE,
        Feature.PROFESSIONAL_SITUATION,
        Feature.PROFESSIONAL_SITUATION_DETAILS,
        Feature.PROFESSIONAL_SITUATION_CREATE,
        Feature.PROFESSIONAL_SITUATION_UPDATE,
        // A follow-up binds a trainer to a learner, not to a classroom: the trainer workspace keeps them.
        Feature.FOLLOW_UPS,
        Feature.FOLLOW_UPS_LIST_LEARNER_SELECT,
        Feature.FOLLOW_UPS_CREATE,
        Feature.FOLLOW_UPS_CREATE_TRAINER,
        Feature.FOLLOW_UPS_EDIT,
        Feature.FOLLOW_UPS_EDIT_TRAINER,
        Feature.FRAMEWORKS,
    ],
    [UserRole.MANAGER]: [...authenticatedFeatures, Feature.ADMIN, Feature.ADMIN_STATISTICS],
};

export const getFeaturesWithoutClassroom = (role: UserRole, features: Set<Feature>): Set<Feature> => {
    const keptFeatures = FEATURES_WITHOUT_CLASSROOM[role];

    return keptFeatures ? Set.intersect([features, Set(keptFeatures)]) : features;
};
